import { Refraction } from '@ca/common'
import { FlowConfig, RemoteConfig } from '@ca/common/types'
import {
  BundleType,
  CustomOptionPrefix,
  SelectedItem,
  StepMode,
  StepsState,
} from '@order/orderTypes'
import { getSelectionState } from '../hooks/useStepsState'
import { resolveVariants } from './resolveVariants'
import { validateItem } from './validateItem'
import { VariablesUtils, VariableValues } from './OrderVariables'
import { getFlow } from './getFlow'
import { debugSteps } from './getDebugLog'

export const endPathname = '/order/summary' as const
export const customizationPathname = '/configuration/lenses' as const

const stepPathname = '/configuration/steps/[stepId]' as const

export type SelectionStateKey<T extends StepMode = StepMode> = `state-${T}`

type StepModeStates = Partial<Record<StepMode, StepsState>>

type StepModeSelection = Partial<Record<SelectionStateKey, string>>

export type NextStep = {
  pathname: string
  params: {
    stepId?: string
    mode?: StepMode
    bundleType: BundleType
    orderItemId: string
  } & StepModeSelection
}

export type NextStepOptions = {
  stepId: string | number | null
  mode?: StepMode
  config: RemoteConfig | null
  /**
   * Current state of the steps pass empty array to start a new configuration
   */
  states: StepModeStates
  item?: SelectedItem
  values: VariableValues
  orderItemId: string | undefined
  refraction: Refraction | undefined | null
  bundleType: BundleType
  endPath: string | undefined
  flowName?: string
}

const getNextMode = (flow: FlowConfig, mode: StepMode) => {
  const currentModeIndex = flow.stepModes.findIndex((m) => m.mode === mode)
  const nextModeConfig = flow.stepModes[currentModeIndex + 1]
  debugSteps.log('getNextMode', flow.name, {
    currentMode: mode,
    nextModeConfig,
    currentModeIndex,
  })
  return nextModeConfig
}

const getNextSelection = (states: StepModeStates) => {
  return Object.fromEntries(
    Object.entries(states).map(([key, value]) => [
      `state-${key}` as SelectionStateKey,
      getSelectionState(value),
    ])
  ) as Partial<Record<SelectionStateKey, string>>
}

/**
 * Use this to build step link,
 * don't manually navigate to steps as it will break the state / automatic steps
 *
 *
 */
export const getNextStepHref = ({
  stepId,
  config,
  mode,
  states,
  item,
  values,
  orderItemId,
  // bundle,
  refraction,
  bundleType,
  endPath,
  flowName,
}: NextStepOptions): NextStep => {
  if (!config) {
    throw new Error('Config is required')
  }

  const flow = getFlow(config, flowName)

  mode = mode ?? flow.stepModes[0]?.mode

  const state = states[mode] ?? []

  const nextStep = stepId ? config.steps[stepId] : undefined

  // Push current item to state and create new item based on matched option
  const nextStates = {
    ...states,
    [mode]: item ? [...state, item] : state,
  }

  // const nextState = getSelectionState(states[mode] ?? [], item)

  const nextSelection = getNextSelection(nextStates)

  const overrides = VariablesUtils.fromArray(item?.o)

  const newValues = item?.var
    ? VariablesUtils.merge(values, overrides, { [item.var]: item.val })
    : values

  if (!nextStep) {
    const nextModeConfig = getNextMode(flow, mode)

    if (nextModeConfig) {
      return getNextStepHref({
        stepId: nextModeConfig.initialStep,
        config,
        values: newValues,
        mode: nextModeConfig.mode,
        states: nextStates,
        orderItemId,
        refraction,
        bundleType,
        endPath,
        flowName,
      })
    }

    return {
      pathname: endPath ?? endPathname,
      params: {
        ...nextSelection,
        bundleType,
        orderItemId: orderItemId || '',
      },
    } as const
  }

  if (nextStep?.template !== 'automatic') {
    return {
      pathname: stepPathname,
      params: {
        stepId: `${nextStep.id}`,
        orderItemId: orderItemId || '',
        ...nextSelection,
        mode,
        bundleType,
      },
    } as const
  }

  /* Resolve automatic next step */

  // Match custom options
  const customOptions = nextStep.customOptions.map(
    (option) => config.customOptions[option]
  )

  const matchedOption = customOptions.filter((option) =>
    validateItem(option, newValues, config.conditions, refraction ?? undefined).result
  )[0]

  if (matchedOption?.nextStep) {
    // We matched a custom option
    return getNextStepHref({
      stepId: matchedOption.nextStep,
      config,
      values: newValues,
      mode,
      // Push current item to state and create new item based on matched option
      states: nextStates,
      item: {
        oId: `${CustomOptionPrefix}:${matchedOption.id}`,
        action: matchedOption.action ?? undefined,
      },
      orderItemId,
      refraction,
      bundleType,
      endPath,
      flowName,
    })
  }

  const variant = resolveVariants({
    config,
    refraction: refraction ?? undefined,
    step: nextStep,
    values: newValues,
  })?.[0]

  if (variant) {
    // We matched a variant so we update state with current item and get href for next step
    return getNextStepHref({
      stepId: nextStep.itemNextStep,
      config,
      mode,
      states: nextStates,
      values: newValues,
      orderItemId,
      item: {
        oId: `${variant.id}`,
        vId: `${variant.active.id}`,
        action: variant.action ?? undefined,
        val: variant.active.value,
        var: variant.variable,
        o: VariablesUtils.toStateArray(variant.active.overrides),
      },
      refraction,
      bundleType,
      endPath,
      flowName,
    })
  }

  const nextModeConfig = getNextMode(flow, mode)

  if (nextModeConfig) {
    return getNextStepHref({
      stepId: nextModeConfig.initialStep,
      config,
      values,
      mode: nextModeConfig.mode,
      states: nextStates,
      orderItemId,
      refraction,
      bundleType,
      endPath,
      flowName,
    })
  }

  return {
    pathname: endPath ?? endPathname,
    params: {
      ...nextSelection,
      bundleType,
      orderItemId: orderItemId || '',
    },
  } as const
}

export const isConfigFinished = (next: NextStep) => {
  return next.pathname === endPathname
}
